import {
	hasFeatureFlag,
	listenToAnyTaskRunUpdated,
	registerAction,
	removeAnyTaskRunUpdatedListener
} from '@bringg-frontend/global-stores';
import { PresetViewSupportedView } from '@bringg/types';
import { getRoot } from 'mobx-easy';
import { IReactionDisposer, reaction, runInAction } from 'mobx';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';

import type { ExceptionFilters } from '../../types/common.consts';
import { TaskAssignedStateEnum } from '../../types/common.consts';
import { APP_CONFIG, CONFIG_TABLE_IDS } from '../../consts';
import { AlertsCounter } from './alert-counter';
import { TableConfigObjectAdapter } from './table-config-object-adapter';
import { defaultOrderedColumnOptions as defaultTaskAlertColumns } from '../../features/exceptions-management/columns/task-alert-columns';
import { defaultOrderedColumnOptions as defaultDriverAlertColumns } from '../../features/exceptions-management/columns/driver-alert-columns';
import { defaultOrderedColumnOptions as defaultOptimisationAlertColumns } from '../../features/exceptions-management/columns/optimisation-alert-columns';
import LoginLogoutReaction from '../core/login-logout-reaction';
import { alertsViewProvider } from 'bringg-web/stores/alerts-view/alerts-view-provider';
import RootStore from '../root-store';
import { PRESETS_VIEWS_FEATURE_FLAG } from 'bringg-web/features/preset-views/utils';
import { presetViewsRoot } from 'bringg-web/features/preset-views/stores/data-providers/preset-views-root';
import { PresetViews } from 'bringg-web/services/cross-application/cross-application.actions';

export const TASK_ALERTS_EMPTY_FILTERS: ExceptionFilters = {
	searchText: '',
	teams: [],
	routes: [],
	alerts: [],
	assignedState: TaskAssignedStateEnum.ALL,
	alertOwner: null,
	companies: [],
	severity: null
};

export const DRIVER_ALERTS_EMPTY_FILTERS: ExceptionFilters = {
	searchText: '',
	alerts: [],
	teams: [],
	companies: [],
	severity: null
};

export const OPTIMIZATION_ALERTS_EMPTY_FILTERS: ExceptionFilters = {
	searchText: '',
	alerts: [],
	teams: [],
	companies: [],
	severity: null
};

class AlertsViewStore extends LoginLogoutReaction {
	// isCustomizeListLayoutAllowed = false;
	// tab badge counters
	readonly unseenAlertsManager = new AlertsCounter();
	// table configs
	taskAlertTableConfig: TableConfigObjectAdapter;
	driverAlertsTableConfig: TableConfigObjectAdapter;
	optimizationAlertsTableConfig: TableConfigObjectAdapter;

	private init() {
		const { filterDefaultColumnsByFeatureFlags } = alertsViewProvider;

		this.taskAlertTableConfig = new TableConfigObjectAdapter(
			CONFIG_TABLE_IDS.EXCEPTIONS,
			TASK_ALERTS_EMPTY_FILTERS,
			filterDefaultColumnsByFeatureFlags(defaultTaskAlertColumns),
			PresetViewSupportedView.OrderAlerts
		);
		this.driverAlertsTableConfig = new TableConfigObjectAdapter(
			CONFIG_TABLE_IDS.DRIVER_ALERTS,
			DRIVER_ALERTS_EMPTY_FILTERS,
			filterDefaultColumnsByFeatureFlags(defaultDriverAlertColumns),
			PresetViewSupportedView.DriverAlerts
		);
		this.optimizationAlertsTableConfig = new TableConfigObjectAdapter(
			CONFIG_TABLE_IDS.OPTIMIZATION_ALERTS,
			OPTIMIZATION_ALERTS_EMPTY_FILTERS,
			filterDefaultColumnsByFeatureFlags(defaultOptimisationAlertColumns),
			PresetViewSupportedView.OptimisationAlerts
		);
	}

	private presetViewsReadyReactionDisposer: IReactionDisposer = null;
	private cleanupActivePresetViewChangedListener: () => void;

	// Consider removing afterUserLoggedIn and initialising internals
	//	only when store is indeed needed
	afterUserLoggedIn() {
		this.init();

		if (APP_CONFIG.APPLY_MERCHANT_TABLE_CONFIG) {
			const {
				usersStore: { currentUser }
			} = getRoot<RootStore>().data;
			const presetViewsEnabled = hasFeatureFlag(currentUser, PRESETS_VIEWS_FEATURE_FLAG);
			if (presetViewsEnabled) {
				const { presetViewsViewStore } = presetViewsRoot.getStore();

				this.presetViewsReadyReactionDisposer = reaction(
					(): boolean => presetViewsViewStore.allLoaded,
					isPresetViewsLoaded => {
						if (isPresetViewsLoaded) {
							// Seems like we need this to be launched only once after data is loaded
							this.taskAlertTableConfig.setupTableConfig(
								this.taskAlertTableConfig.getPresetViewTableConfig()
							);
							this.driverAlertsTableConfig.setupTableConfig(
								this.driverAlertsTableConfig.getPresetViewTableConfig()
							);
							this.optimizationAlertsTableConfig.setupTableConfig(
								this.optimizationAlertsTableConfig.getPresetViewTableConfig()
							);
						}
					}
				);

				// todo: consider to move the preset logic reaction to component level (parent table components)
				this.cleanupActivePresetViewChangedListener = registerAction(
					PresetViews.PRESET_VIEWS_UPDATED,
					event => {
						runInAction(() => {
							if (event?.view === PresetViewSupportedView.OrderAlerts) {
								this.taskAlertTableConfig.setupTableConfig(
									this.taskAlertTableConfig.getPresetViewTableConfig()
								);
							}
							if (event?.view === PresetViewSupportedView.DriverAlerts) {
								this.driverAlertsTableConfig.setupTableConfig(
									this.driverAlertsTableConfig.getPresetViewTableConfig()
								);
							}
							if (event?.view === PresetViewSupportedView.OptimisationAlerts) {
								this.optimizationAlertsTableConfig.setupTableConfig(
									this.optimizationAlertsTableConfig.getPresetViewTableConfig()
								);
							}
						});
					},
					getRootEnv().dashboardSdk.sdk.crossAppTransport
				);
			} else {
				this.taskAlertTableConfig.setupTableConfig();
				this.driverAlertsTableConfig.setupTableConfig();
				this.optimizationAlertsTableConfig.setupTableConfig();
			}
		}
	}

	afterUserLoggedOut() {
		this.taskAlertTableConfig?.disposeReactions();
		this.driverAlertsTableConfig?.disposeReactions();
		this.optimizationAlertsTableConfig?.disposeReactions();
		this.presetViewsReadyReactionDisposer();
		this.cleanupActivePresetViewChangedListener();
		this.cleanupActivePresetViewChangedListener = () => undefined;
	}

	afterRootInit() {
		super.afterRootInit();
		// to avoid rerender the filters on alert screen on each update task, filters should updates only if route updates
		// TODO: also keep in mind that the run id can be changed, but there is no alerts for this updated task => no need to update the store
		listenToAnyTaskRunUpdated(this.updateRouteFilterValues);
	}

	resetAfterRemoveRoot() {
		removeAnyTaskRunUpdatedListener(this.updateRouteFilterValues);

		this.taskAlertTableConfig?.resetAfterRemoveRoot();
		this.driverAlertsTableConfig?.resetAfterRemoveRoot();
		this.optimizationAlertsTableConfig?.resetAfterRemoveRoot();
	}

	updateRouteFilterValues = () => {
		this.taskAlertTableConfig.filterOutNotRelevantRoutesFromFilter();
	};
}

export default AlertsViewStore;
